import React from 'react';
import PropTypes from 'prop-types';
import { Stack, TextField } from '@mui/material';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from 'dayjs';

/**
 * SharePriceForm Component
 * 
 * Renders the form fields for updating share prices.
 * Handles displaying input fields, validation states, and user input.
 
 * @param {Object} props - Component props
 * @param {Object} props.formData - The current form values
 * @param {Object} props.formErrors - Current validation errors
 * @param {Function} props.onFieldChange - Callback for field changes
 */
const SharePriceForm = ({ formData, formErrors, onFieldChange }) => {
  const { valuationDate, unitValue, notes } = formData;
  const { unitValueError } = formErrors;

  /**
   * Handles date field updates
   * @param {Object} selectedDate - The selected date object from the date picker
   */
  const handleUpdateDate = (selectedDate) => {
    if (!selectedDate || !selectedDate.$d) return;
    const date = new Date(selectedDate.$d);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const formattedDate = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
    
    onFieldChange('valuationDate', formattedDate);
  };
  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={2}>
        <DatePicker
          sx={{ flex: 1 }}
          id="valuationDate"
          name="valuationDate"
          label="Valuation Date"
          variant="outlined"
          value={dayjs(valuationDate)}
          fullWidth
          onChange={handleUpdateDate}
          renderInput={(params) => <TextField {...params} />}
        />
        
        <TextField
          sx={{ flex: 1 }}
          id="unitValue"
          name="unitValue"
          type="number"
          label="Unit Value"
          variant="outlined"
          value={unitValue}
          onChange={(e) => onFieldChange('unitValue', e.target.value)}
          error={unitValueError}
          helperText={unitValueError ? "Unit value should be 0 or more" : ""}
          fullWidth
          InputProps={{ inputProps: { min: 0.01, step: 0.01 } }}
        />
      </Stack>
      
      <TextField
        sx={{ flex: 1 }}
        id="notes"
        name="notes"
        label="Remarks"
        multiline
        rows={4}
        value={notes}
        onChange={(e) => onFieldChange('notes', e.target.value)}
        fullWidth
      />
    </Stack>
  );
};

SharePriceForm.propTypes = {
  formData: PropTypes.shape({
    valuationDate: PropTypes.string.isRequired,
    unitValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    notes: PropTypes.string.isRequired
  }).isRequired,
  formErrors: PropTypes.shape({
    unitValueError: PropTypes.bool.isRequired
  }).isRequired,
  onFieldChange: PropTypes.func.isRequired
};

export default SharePriceForm;